// Shared scene contract — the spatial + data layout every Act builds against.
// The world is vertical: the Core sits at y=0; the Foundation reservoir sits below
// at FOUNDATION_Y. The camera descends from the Core into the Foundation on scroll.

import { COLORS, PHI, FONTS } from '../theme'

/** Shared props every Act component receives. */
export type ActProps = { reduced?: boolean; mobile?: boolean }

/** Outcome data. EXACT customer-facing names only — never codenames. */
export type Outcome = {
  id: 'recover' | 'close' | 'beFound' | 'reputation'
  label: string
  /** small honesty tag rendered beside the label, e.g. Reputation is "coming online" */
  tag?: string
  promise: string
  /** longer honest "how it works" shown when the outcome is focused */
  detail: string
  /** maturity disclosure shown on static surfaces (honesty guardrail) */
  maturity?: string
  /** orbital angle (radians) around the Core */
  angle: number
}

export const OUTCOMES: Outcome[] = [
  {
    id: 'recover',
    label: 'Recover',
    promise: 'Reach the business you already have.',
    detail:
      'We go after the dead and dormant leads already sitting in your CRM — enriched, contacted compliantly by email and text, and handed back to you warm. The defensive win: revenue you already paid for, no new ad spend.',
    maturity: 'Live as a send-gated pilot on K2 Sells.',
    angle: -Math.PI / 2,
  },
  {
    id: 'close',
    label: 'Close',
    promise: 'Close every deal clean and on time.',
    detail:
      'It reads the executed contract, not a checklist — tracking the real negotiated dates, the pace of a healthy deal, and whether the other side has gone quiet, and catching the small paperwork errors that become liability. It drafts every nudge; a human always sends.',
    maturity: 'Running today on K2 Sells’ real deals — draft-only, single-brokerage.',
    angle: 0,
  },
  {
    id: 'beFound',
    label: 'Be Found',
    promise: 'Make your market come looking for you.',
    detail:
      'Every page is built to be cited — in Google and in the assistants buyers ask first — grounded in primary local records a late copycat can’t fake. One exclusive seat per market: we open a market only where we can prove the engine first.',
    maturity: 'A live but early engine; the citation schema and the measure-and-aim loop are the proven levers.',
    angle: Math.PI / 2,
  },
  {
    id: 'reputation',
    label: 'Reputation',
    tag: 'coming online',
    promise: 'Turn every closing into proof the next client can see.',
    detail:
      'Built to turn a clean closing into a compliant, on-brand review ask in your voice — the happy client routed to public proof, the response drafted for your approval. A fair-housing gate sits on everything, and a human approves before anything reaches a client.',
    maturity: 'Built, not yet live — switches on with Google Business Profile verification.',
    angle: Math.PI,
  },
]

/** The Foundation's three pillars (customer-facing names only). */
export const FOUNDATION_PILLARS = [
  { id: 'data', label: 'The data engine', note: 'receipt-backed · sourced, not guessed' },
  { id: 'brain', label: 'Your brain', note: 'your voice, captured once' },
  { id: 'sovereignty', label: 'Sovereignty', note: 'your data stays yours — never hostage' },
]

// ---- Spatial layout ----
export const CORE_RADIUS = 1.0
export const ORBIT_RADIUS = 3.4 // outcome constellations orbit radius (~ CORE * phi^?)
export const FOUNDATION_Y = -16 // reservoir center, below the core
export const HORIZON_Y = -4.2 // the surface line between world-above and reservoir-below

// ---- Scroll choreography (offset 0..1 across the journey) ----
// Act ranges let each component map global scroll → its own local 0..1 "focus".
// 5 full-screen copy beats → PAGES = 5 so each beat lands on a clean offset:
// beat0≈0.0 (reveal) · beat1≈0.25 (core) · beat2≈0.5 (reputation) ·
// beat3≈0.75 (foundation) · beat4≈1.0 (sovereignty close).
export const SCROLL = {
  reveal: [0.0, 0.18] as [number, number], // flower-of-life unfold → core forms
  core: [0.12, 0.52] as [number, number], // orbit + flywheel, grab-and-spin live here
  descent: [0.5, 0.72] as [number, number], // camera tilts down through the horizon
  foundation: [0.7, 1.0] as [number, number], // reservoir + springs feed the outcomes
}
export const PAGES = 5 // ScrollControls page count — one per copy beat

/** Map a global scroll offset to a local 0..1 progress within [a,b]. */
export function focusOf(offset: number, [a, b]: [number, number]): number {
  if (b <= a) return offset >= b ? 1 : 0
  return Math.min(1, Math.max(0, (offset - a) / (b - a)))
}

// ---- THREE-ready color strings (hex) re-exported for convenience ----
export { COLORS, PHI, FONTS }
